import React, { useState, useEffect, useContext } from "react";
import { FaTimes } from "react-icons/fa";
import { FaChevronDown, FaChevronUp, FaDownload } from "react-icons/fa";
import { UserContext } from "../context/UserContext";
import { useLoading } from "../context/LoadingContext";

const VotedBillsReport = ({ onClose }) => {
  const { emailId, isLoggedIn, phoneNo, state } = useContext(UserContext);
  // const { showLoadingWithDelay, hideLoading } = useLoading();
  console.log("Current emailId:", emailId); // Debug log

  const [reports, setReports] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState(
    new Set([emailId === "info@urcommunity.com.au" ? 'Voted' : 'Federal Bills'])
  );
  const [expandedFilters, setExpandedFilters] = useState(new Set(['Filter by People']));
  const [activeSubTab, setActiveSubTab] = useState('Federal');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [territoryData, setTerritoryData] = useState({});
  const [isLoadingTerritory, setIsLoadingTerritory] = useState(false);

  const territories = [
    { short: 'Federal', full: 'Federal' },
    { short: 'NSW', full: 'New South Wales' },
    { short: 'QLD', full: 'Queensland' },
    { short: 'VIC', full: 'Victoria' },
    { short: 'ACT', full: 'Australian Capital Territory' },
    { short: 'WA', full: 'Western Australia' },
    { short: 'SA', full: 'South Australia' },
    { short: 'NT', full: 'Northern Territory' },
    { short: 'TAS', full: 'Tasmania' }
  ];

  const territoryFullNames = {
    'Federal': 'Federal',
    'VIC': 'Victoria',
    'NSW': 'New South Wales',
    'QLD': 'Queensland',
    'WA': 'Western Australia',
    'SA': 'South Australia',
    'TAS': 'Tasmania',
    'ACT': 'Australian Capital Territory',
    'NT': 'Northern Territory'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // showLoadingWithDelay(); // Show loading after 2 seconds if the request is still pending
        if (!isLoggedIn || !emailId || !phoneNo || !state) {
          console.log("Missing required data:", { isLoggedIn, emailId, phoneNo, state });
          return;
        }

        console.log("Fetching reports for:", emailId);
        const response = await fetch(
          `/api/voted-bills-reports?phoneNo=${phoneNo}&state=${state}&emailId=${emailId}`
        );
        if (!response.ok) throw new Error("Failed to fetch voted bills reports");
        const data = await response.json();
        console.log("Received data:", data); // Debug log
        setReports(data);
      } catch (error) {
        console.error("Error fetching voted bills reports:", error);
      } finally {
        // hideLoading(); // Hide loading spinner regardless of success or failure
        setIsLoading(false);
      }
    };

    if (isLoggedIn && phoneNo) {
      fetchData();
    }
  }, [isLoggedIn, phoneNo, state, emailId]);

  // Add new useEffect for territory data fetching
  useEffect(() => {
    if (!activeSubTab) return;

    const fetchTerritoryData = async () => {
      setIsLoadingTerritory(true);
      try {
        // Get the full territory name from the short name
        const fullTerritoryName = territoryFullNames[activeSubTab] || activeSubTab;
        console.log('Active Tab:', activeSubTab);
        console.log('Full Territory Name being sent to API:', fullTerritoryName);
        
        const response = await fetch(
          `/api/territory-bills-data?territory=${encodeURIComponent(fullTerritoryName)}`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch ${fullTerritoryName} data`);
        }
        
        const data = await response.json();
        console.log('API Response Data:', data);
        console.log(`Total bills received for ${fullTerritoryName}:`, data.bills?.length || 0);
        
        setTerritoryData(prevData => ({
          ...prevData,
          [activeSubTab]: data
        }));
      } catch (error) {
        console.error(`Error fetching ${activeSubTab} data:`, error);
      } finally {
        setIsLoadingTerritory(false);
      }
    };

    fetchTerritoryData();
  }, [activeSubTab]);

  // Define section types based on email
  const sectionTypes = emailId === "info@urcommunity.com.au" 
    ? ['Voted', 'Not Voted']
    : ['Federal Bills', `State Bills`];

  console.log("Section types:", sectionTypes); // Debug log
  console.log("Reports data:", reports); // Debug log
  console.log("Expanded sections:", expandedSections); // Debug log

  const handleDownload = async (e, sectionType, format) => {
    e.stopPropagation();
    try {
      console.log(`Initiating ${format} download for ${sectionType}`);
      
      const response = await fetch(
        `/api/download-bills-report?` + 
        `phoneNo=${phoneNo}&state=${state}&type=${sectionType}&format=${format}`
      );

      if (!response.ok) {
        throw new Error(`Failed to download ${format} report`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sectionType.replace(' ', '_')}_Report.${format.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log(`Successfully downloaded ${format} report for ${sectionType}`);
    } catch (error) {
      console.error(`Error downloading ${format} report:`, error);
    }
  };

  const toggleFilter = (filterType) => {
    setExpandedFilters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filterType)) {
        newSet.delete(filterType);
      } else {
        newSet.add(filterType);
      }
      return newSet;
    });
  };

  const toggleSection = (sectionType) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionType)) {
        newSet.delete(sectionType);
      } else {
        newSet.add(sectionType);
      }
      return newSet;
    });
  };

  const toggleDropdown = (sectionType, e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === sectionType ? null : sectionType);
  };

  const renderBarGraphs = (sectionType) => {
    return (
      <div style={{ 
        marginTop: '30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '30px'
      }}>
        {/* People Bar Graph */}
        <div style={{
          border: '1px solid #dee2e6',
          padding: '20px',
          borderRadius: '5px'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '20px' }}>People Voting Distribution</h4>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px'
          }}>
            <div style={{ width: '100px' }}>Yes Votes:</div>
            <div style={{
              height: '30px',
              backgroundColor: '#28a745',
              width: `${(reports[sectionType]?.yesCount || 0) / ((reports[sectionType]?.yesCount || 0) + (reports[sectionType]?.noCount || 0)) * 100}%`,
              minWidth: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 10px',
              color: 'white'
            }}>
              {reports[sectionType]?.yesCount || 0}
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{ width: '100px' }}>No Votes:</div>
            <div style={{
              height: '30px',
              backgroundColor: '#dc3545',
              width: `${(reports[sectionType]?.noCount || 0) / ((reports[sectionType]?.yesCount || 0) + (reports[sectionType]?.noCount || 0)) * 100}%`,
              minWidth: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 10px',
              color: 'white'
            }}>
              {reports[sectionType]?.noCount || 0}
            </div>
          </div>
        </div>

        {/* Bills Bar Graph */}
        <div style={{
          border: '1px solid #dee2e6',
          padding: '20px',
          borderRadius: '5px'
        }}>
          <h4 style={{ marginTop: 0, marginBottom: '20px' }}>Bills Distribution</h4>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px'
          }}>
            <div style={{ width: '100px' }}>Passed:</div>
            <div style={{
              height: '30px',
              backgroundColor: '#17a2b8',
              width: `${(reports[sectionType]?.passedCount || 0) / ((reports[sectionType]?.passedCount || 0) + (reports[sectionType]?.failedCount || 0)) * 100}%`,
              minWidth: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 10px',
              color: 'white'
            }}>
              {reports[sectionType]?.passedCount || 0}
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <div style={{ width: '100px' }}>Failed:</div>
            <div style={{
              height: '30px',
              backgroundColor: '#6c757d',
              width: `${(reports[sectionType]?.failedCount || 0) / ((reports[sectionType]?.passedCount || 0) + (reports[sectionType]?.failedCount || 0)) * 100}%`,
              minWidth: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 10px',
              color: 'white'
            }}>
              {reports[sectionType]?.failedCount || 0}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBillsDistributionGraph = (currentTerritoryData) => {
    return (
      <div style={{
        border: '1px solid #dee2e6',
        padding: '20px',
        borderRadius: '5px',
        marginTop: '30px'
      }}>
        <h4 style={{ marginTop: 0, marginBottom: '20px' }}>Bills Distribution</h4>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          {currentTerritoryData.map((bill, index) => (
            <div key={`${bill.title}-${index}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minHeight: '40px'
            }}>
              <div style={{ 
                width: '200px', 
                fontSize: '0.9rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {bill.title}
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                gap: '5px'
              }}>
                {/* Yes votes bar */}
                <div style={{
                  height: '25px',
                  backgroundColor: '#17a2b8',
                  width: `${(bill.yesCount / (bill.yesCount + bill.noCount || 1)) * 100}%`,
                  minWidth: bill.yesCount > 0 ? '30px' : '0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem',
                  borderRadius: '3px'
                }}>
                  {bill.yesCount > 0 && bill.yesCount}
                </div>
                {/* No votes bar */}
                <div style={{
                  height: '25px',
                  backgroundColor: '#6c757d',
                  width: `${(bill.noCount / (bill.yesCount + bill.noCount || 1)) * 100}%`,
                  minWidth: bill.noCount > 0 ? '30px' : '0px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.8rem',
                  borderRadius: '3px'
                }}>
                  {bill.noCount > 0 && bill.noCount}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFilters = (sectionType) => {
    if (emailId !== "info@urcommunity.com.au" || !expandedSections.has(sectionType)) {
      return null;
    }

    const currentTerritoryData = territoryData[activeSubTab]?.bills || [];

    return (
      <div style={{ 
        marginTop: '20px',
        width: '98%',
        marginLeft: 'auto',    
        marginRight: 'auto',   
        paddingLeft: '15px',
        paddingRight: '15px'   
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'nowrap',
          borderBottom: '1px solid #dee2e6',
          marginBottom: '20px',
          gap: '2px',
          width: '100%',
          overflowX: 'auto',
          paddingBottom: '2px'
        }}>
          {territories.map(territory => (
            <div
              key={territory.full}
              onClick={() => {
                console.log('Territory clicked:', territory.full); // Debug log
                setActiveSubTab(territory.full);
              }}
              style={{
                padding: '10px 15px',
                cursor: 'pointer',
                backgroundColor: activeSubTab === territory.full ? '#fff' : '#f8f9fa',
                border: '1px solid #dee2e6',
                borderBottom: activeSubTab === territory.full ? '1px solid #fff' : '1px solid #dee2e6',
                borderRadius: '5px 5px 0 0',
                position: 'relative',
                bottom: '-1px',
                fontWeight: activeSubTab === territory.full ? 'bold' : 'normal',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                flex: '1',
                justifyContent: 'center'
              }}
            >
              {territory.short}
              <FaDownload style={{ fontSize: '0.9rem', color: '#007bff' }} />
            </div>
          ))}
        </div>

        <div style={{ 
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #000'
        }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            backgroundColor: '#fff'
          }}>
            <thead style={{ 
              position: 'sticky', 
              top: 0,
              backgroundColor: '#fff'
            }}>
              <tr>
                <th rowSpan="2" style={{ 
                  padding: "5px",
                  border: '1px solid #000',
                  textAlign: "center",
                  height: '30px'
                }}>Bill Title</th>
                <th rowSpan="2" style={{ 
                  padding: "5px",
                  border: '1px solid #000',
                  textAlign: "center",
                  height: '30px'
                }}>Category</th>
                <th colSpan="2" style={{ 
                  padding: "5px",
                  border: '1px solid #000',
                  textAlign: "center",
                  height: '30px'
                }}>User Vote</th>
              </tr>
              <tr>
                <th style={{ 
                  padding: "5px",
                  border: '1px solid #000',
                  textAlign: "center",
                  height: '30px'
                }}>Yes count</th>
                <th style={{ 
                  padding: "5px",
                  border: '1px solid #000',
                  textAlign: "center",
                  height: '30px'
                }}>No count</th>
              </tr>
            </thead>
            <tbody>
              {currentTerritoryData.map((bill) => (
                <tr key={bill.title}>
                  <td style={{ 
                    padding: "10px",
                    border: '1px solid #000'
                  }}>{bill.title}</td>
                  <td style={{ 
                    padding: "10px",
                    border: '1px solid #000',
                    textAlign: "center"
                  }}>{bill.category || '-'}</td>
                  <td style={{ 
                    padding: "10px",
                    border: '1px solid #000',
                    textAlign: "center"
                  }}>{bill.yesCount || 0}</td>
                  <td style={{ 
                    padding: "10px",
                    border: '1px solid #000',
                    textAlign: "center"
                  }}>{bill.noCount || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bills Distribution Graph */}
        {renderBillsDistributionGraph(currentTerritoryData)}

        {/* Bar Graphs */}
        <div style={{ 
          marginTop: '30px',
          display: 'flex',
          flexDirection: 'column',
          gap: '30px'
        }}>
          {/* People Bar Graph */}
          <div style={{
            border: '1px solid #dee2e6',
            padding: '20px',
            borderRadius: '5px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '20px' }}>People Voting Distribution</h4>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '10px'
            }}>
              <div style={{ width: '100px' }}>Yes Votes:</div>
              <div style={{
                height: '30px',
                backgroundColor: '#28a745',
                width: `${(reports[sectionType]?.yesCount || 0) / ((reports[sectionType]?.yesCount || 0) + (reports[sectionType]?.noCount || 0)) * 100}%`,
                minWidth: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 10px',
                color: 'white'
              }}>
                {reports[sectionType]?.yesCount || 0}
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{ width: '100px' }}>No Votes:</div>
              <div style={{
                height: '30px',
                backgroundColor: '#dc3545',
                width: `${(reports[sectionType]?.noCount || 0) / ((reports[sectionType]?.yesCount || 0) + (reports[sectionType]?.noCount || 0)) * 100}%`,
                minWidth: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 10px',
                color: 'white'
              }}>
                {reports[sectionType]?.noCount || 0}
              </div>
            </div>
          </div>

          {/* Bills Bar Graph */}
          <div style={{
            border: '1px solid #dee2e6',
            padding: '20px',
            borderRadius: '5px'
          }}>
            <h4 style={{ marginTop: 0, marginBottom: '20px' }}>Bills Distribution</h4>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '10px'
            }}>
              <div style={{ width: '100px' }}>Passed:</div>
              <div style={{
                height: '30px',
                backgroundColor: '#17a2b8',
                width: `${(reports[sectionType]?.passedCount || 0) / ((reports[sectionType]?.passedCount || 0) + (reports[sectionType]?.failedCount || 0)) * 100}%`,
                minWidth: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 10px',
                color: 'white'
              }}>
                {reports[sectionType]?.passedCount || 0}
              </div>
            </div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <div style={{ width: '100px' }}>Failed:</div>
              <div style={{
                height: '30px',
                backgroundColor: '#6c757d',
                width: `${(reports[sectionType]?.failedCount || 0) / ((reports[sectionType]?.passedCount || 0) + (reports[sectionType]?.failedCount || 0)) * 100}%`,
                minWidth: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 10px',
                color: 'white'
              }}>
                {reports[sectionType]?.failedCount || 0}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSectionHeader = (sectionType) => (
    <div 
      onClick={() => toggleSection(sectionType)}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px",
        backgroundColor: "#f8f9fa",
        cursor: "pointer",
        borderRadius: "5px",
      }}
    >
      <div style={{
        display: "flex",
        alignItems: "center",
      }}>
        <h4 style={{ margin: 0 }}>{sectionType}</h4>
        {emailId !== "info@urcommunity.com.au" && (
          <div style={{ position: 'relative', marginLeft: '10px' }}>
            <FaDownload 
              onClick={(e) => toggleDropdown(sectionType, e)}
              style={{ 
                cursor: "pointer",
                fontSize: "1.2rem",
                color: "#007bff"
              }} 
            />
            {activeDropdown === sectionType && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                backgroundColor: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '100px'
              }}>
                {['PDF', 'CSV', 'Excel'].map(format => (
                  <div
                    key={format}
                    onClick={(e) => handleDownload(e, sectionType, format)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      hover: {
                        backgroundColor: '#f8f9fa'
                      }
                    }}
                  >
                    {format}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        {expandedSections.has(sectionType) ? 
          <FaChevronUp color="#6c757d" /> : 
          <FaChevronDown color="#6c757d" />
        }
      </div>
    </div>
  );

  return (
    <div style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 999,
    }}>
      <div style={{
        backgroundColor: "#fff",
        borderRadius: "10px",
        padding: "20px",
        width: "95%",
        maxWidth: "900px",
        maxHeight: "90vh",
        overflowY: "auto",
        position: "relative",
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          borderBottom: "2px solid #eee",
          paddingBottom: "10px",
        }}>
          <h3 style={{ margin: 0 }}>Voted Bills Report</h3>
          <FaTimes onClick={onClose} style={{ cursor: "pointer", fontSize: "1.5rem" }} />
        </div>

        {isLoading ? (
          <p style={{ textAlign: "center" }}>Loading voted bills reports...</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            {sectionTypes.map((sectionType) => {
              console.log("Rendering section:", sectionType); // Debug log
              console.log("Is admin and Voted section?", 
                emailId === "info@urcommunity.com.au" && sectionType === "Voted"); // Debug log
              
              return (
                <div key={sectionType} style={{ marginBottom: "15px" }}>
                  {renderSectionHeader(sectionType)}
                  
                  {expandedSections.has(sectionType) && (
                    <>
                      {emailId === "info@urcommunity.com.au" && sectionType === "Voted" ? (
                        renderFilters(sectionType)
                      ) : (
                        <div style={{ marginTop: "10px" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                              <tr style={{ backgroundColor: "#007bff", color: "#fff" }}>
                                <th style={{ padding: "10px", textAlign: "left" }}>Bill Title</th>
                                <th style={{ padding: "10px", textAlign: "center" }}>
                                  {emailId === "info@urcommunity.com.au" ? "Status" : "Vote"}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(reports[sectionType] || {}).map(([billName, data]) => (
                                <tr key={billName} style={{ borderBottom: "1px solid #dee2e6" }}>
                                  <td style={{ padding: "10px" }}>{billName}</td>
                                  <td style={{ padding: "10px", textAlign: "center" }}>
                                    {emailId === "info@urcommunity.com.au" ? data.status : data.vote}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VotedBillsReport;